import { Component, OnInit } from '@angular/core';
import { BaseComponent } from '../../../base/base.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { TabPanelComponent } from '../../../core/panels/tab-panel/tab-panel.component';
import { TabComponent } from '../../../core/panels/tab-panel/tab.component';
import { PolicyAttributesComponent } from '../../policy-details/policy-attributes.component';
import { PolicyAggregateLimitComponent } from './aggregate-limit/aggregate-limit.component';
import { PolicyCoveragesComponent } from './coverages/coverages.component';
import { PolicyEndorsementComponent } from './endorsement/endorsement-policy.component';
import { PolicyParticipantsComponent } from './participants/participants.component';
import { PolicyDetailComponent } from './policy-detail/policy-detail.component';
import { PolicyRiskUnitComponent } from './risk-unit/risk-unit.component';
import { PolicyDetailsResponse } from '../../../../models/api/view/claim/details/policy-details/policy-details-response';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';

@Component({
  selector: 'app-policy-detail-page',
  imports: [
    PageHeaderComponent,
    DetailPanelComponent,
    DetailItemComponent,
    TabPanelComponent,
    TabComponent,
    PolicyDetailComponent,
    PolicyAggregateLimitComponent,
    PolicyAttributesComponent,
    PolicyParticipantsComponent,
    PolicyCoveragesComponent,
    PolicyRiskUnitComponent,
    PolicyEndorsementComponent,
  ],
  template: `<app-page-header label="Policy Details" />
    <div class="tw-flex tw-gap-8">
      <div class="tw-basis-1/3">
        <app-detail-panel [dataSource]="policyDetails.details">
          <app-detail-item label="Policy Number" name="policyNumber" />
          <app-detail-item label="Policy Name" name="policyName" />
          <app-detail-item label="Policy Type" name="policyType" />
        </app-detail-panel>
      </div>
    </div>
    @if (activeTab) {
      <app-tab-panel [value]="activeTab">
        <app-tab label="Detail">
          <ng-template #content>
            <app-policy-detail [dataSource]="policyDetails.details" />
          </ng-template>
        </app-tab>
        <app-tab
          label="Aggregate Limits"
          [count]="policyDetails.aggregateLimits.length"
        >
          <ng-template #content>
            <app-aggregate-limit [dataSource]="policyDetails.aggregateLimits" />
          </ng-template>
        </app-tab>
        <app-tab label="Attributes" [count]="policyDetails.attributes.length">
          <ng-template #content>
            <app-policy-attributes
              [dataSource]="policyDetails.attributes"
              [showEmpty]="true"
              [source]="claimKey?.lineOfBusiness"
              layout="policy"
            />
          </ng-template>
        </app-tab>
        <app-tab
          label="Participants"
          [count]="policyDetails.participants.length"
        >
          <ng-template #content>
            <app-participants [dataSource]="policyDetails.participants" />
          </ng-template>
        </app-tab>
        <app-tab label="Coverages" [count]="policyDetails.coverages.length">
          <ng-template #content>
            <app-coverages
              [dataSource]="policyDetails.coverages"
              [lineOfBusiness]="claimKey?.lineOfBusiness"
            />
          </ng-template>
        </app-tab>
        <app-tab label="Risk Unit" [count]="policyDetails.riskUnits.length">
          <ng-template #content>
            <app-risk-unit
              [dataSource]="policyDetails.riskUnits"
              [lineOfBusiness]="claimKey?.lineOfBusiness"
            />
          </ng-template>
        </app-tab>
        <app-tab
          label="Endorsements"
          [count]="policyDetails.endorsements.length"
        >
          <ng-template #content>
            <app-endorsement
              [dataSource]="policyDetails.endorsements"
              [lineOfBusiness]="claimKey?.lineOfBusiness"
            />
          </ng-template>
        </app-tab>
      </app-tab-panel>
    }`,
  styles: [],
})
export class PolicyDetailPageComponent extends BaseComponent implements OnInit {
  claimKey: ClaimRow;
  activeTab: string;

  // Initialize with empty structure - data will be loaded from API
  policyDetails: PolicyDetailsResponse = {
    details: {},
    aggregateLimits: [],
    attributes: [],
    participants: [],
    coverages: [],
    riskUnits: [],
    endorsements: [],
  };

  async ngOnInit(): Promise<void> {
    this.view.showLoading();
    await this.loadPolicyDetails();
    this.activeTab = 'Detail';
    this.view.hideLoading();
  }

  private async loadPolicyDetails(): Promise<void> {
    this.claimKey = this.view.currentClaim();

    const response = await this.api.policyDetails.get({
      recordId: this.claimKey.recordId,
      recordSource: this.claimKey.recordSource,
    });

    // Handle null response (when no data found in database)
    if (response) {
      this.policyDetails = response;
    }
  }
}
